import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { QuoteSummary } from '@shared/types/index.ts';
import { quoteApi } from '../../api/endpoints.ts';
import { useSession } from '../../state/session.tsx';
import { ConfirmDialog } from '../ui.tsx';

/** UI confirmation only: DELETE remains the authority for location, money
 * and linked-job restrictions. No linked records are changed by this flow. */
export function DeleteQuoteDialog({
  quote,
  onCancel,
  onDeleted,
}: {
  quote: QuoteSummary;
  onCancel: () => void;
  onDeleted: (quote: QuoteSummary) => void;
}) {
  const { t } = useTranslation();
  const { can } = useSession();
  const [stage, setStage] = useState<'review' | 'confirm'>('review');
  const [acknowledged, setAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // React's disabled state lands on the next render. Guard the request in
  // the same tick as well, including repeated keyboard/pointer activation.
  const submitting = useRef(false);
  const opener = useRef(document.activeElement);
  useEffect(() => {
    const element = opener.current;
    return () => {
      // The second dialog remounts to reset focus to its safe answer. Restore
      // the original list action after either stage closes, if it still exists.
      queueMicrotask(() => {
        if (element instanceof HTMLElement && element.isConnected) element.focus();
      });
    };
  }, []);

  async function remove() {
    if (submitting.current || stage !== 'confirm' || !acknowledged || !can('quotes:delete')) return;
    submitting.current = true;
    setBusy(true);
    setError(null);
    try {
      await quoteApi.remove(quote.id);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('quotes.delete.error'));
      setAcknowledged(false);
      setBusy(false);
      submitting.current = false;
      return;
    }
    onDeleted(quote);
  }

  return (
    <ConfirmDialog
      key={stage}
      title={t(stage === 'review' ? 'quotes.delete.reviewTitle' : 'quotes.delete.confirmTitle', {
        number: quote.quoteNumber,
      })}
      initialFocus="cancel"
      confirmLabel={t(stage === 'review' ? 'quotes.delete.continue' : 'quotes.delete.action')}
      confirmVariant={stage === 'review' ? 'primary' : 'danger'}
      confirmDisabled={!can('quotes:delete') || (stage === 'confirm' && !acknowledged)}
      busy={busy}
      error={error}
      onCancel={() => {
        if (!submitting.current) onCancel();
      }}
      onConfirm={() => {
        if (stage === 'review') setStage('confirm');
        else void remove();
      }}
    >
      <p className="font-semibold">{quote.customerNameSnapshot}</p>
      {quote.propertyAddressSnapshot ? <p>{quote.propertyAddressSnapshot}</p> : null}
      {stage === 'review' ? (
        <div className="mt-3 space-y-3">
          <p>{t('quotes.delete.effect')}</p>
          <p>{t('quotes.delete.retained')}</p>
          <p>{t('quotes.delete.restrictions')}</p>
        </div>
      ) : (
        <div className="mt-3 space-y-3">
          <p>{t('quotes.delete.finalWarning')}</p>
          <label className="flex min-h-11 cursor-pointer items-start gap-3 py-2">
            <input
              type="checkbox"
              className="mt-1 shrink-0"
              checked={acknowledged}
              disabled={busy}
              onChange={(event) => setAcknowledged(event.target.checked)}
            />
            <span>{t('quotes.delete.acknowledge', { number: quote.quoteNumber })}</span>
          </label>
        </div>
      )}
    </ConfirmDialog>
  );
}
